import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible Xi modal.
 * - Portals to document.body so legacy stacking contexts cannot clip it.
 * - Traps focus, restores focus on close, closes on Escape/backdrop.
 */
export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeLabel = 'Close',
  className = '',
  hideClose = false,
}) {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const focusFirst = () => {
      if (!panel) return;
      const nodes = panel.querySelectorAll(FOCUSABLE);
      const target = nodes.length ? nodes[0] : panel;
      target.focus({ preventScroll: true });
    };
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (onClose) onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const nodes = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (node) => node.offsetParent !== null || node === document.activeElement
      );
      if (!nodes.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (restoreRef.current && typeof restoreRef.current.focus === 'function') {
        restoreRef.current.focus({ preventScroll: true });
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass = size === 'wide' ? ' xi-modal--wide' : size === 'full' ? ' xi-modal--full' : '';

  return createPortal(
    <div
      className="xi-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && onClose) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`xi-modal${sizeClass} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
      >
        {(title || !hideClose) && (
          <div className="xi-modal__head">
            <div className="xi-modal__head-main">
              {title ? <h2 id={titleId}>{title}</h2> : null}
              {description ? <p id={descId}>{description}</p> : null}
            </div>
            {!hideClose ? (
              <button type="button" className="xi-icon-btn" onClick={onClose} aria-label={closeLabel}>
                <LuX aria-hidden="true" />
              </button>
            ) : null}
          </div>
        )}
        <div className="xi-modal__body">{children}</div>
        {footer ? <div className="xi-modal__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}
